import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  withStyles
} from '@material-ui/core';
import React, { useState } from 'react';
import { CollapsibleSection } from '../../common/collapsible-section';
import * as styles from './attribute-row.module.scss';
import * as segmentsStyles from '../segments.module.scss';
import { useParams } from 'react-router-dom';
import { useAvailableCompositions } from '../../../hooks/useAvailableCompositions';
import { SegmentAttributeOperator } from '../../../models/segments';
import clsx from 'clsx';
import { colors, blue } from '../../../theme/colors';
import { useBuildSegmentComposition } from './useBuildSegment';
import { MultiselectChip } from './multiselect-chip';
import { Clear } from '@material-ui/icons';
import * as buildStyles from './build-segment.module.scss';
import { SecondaryAlert, TertiaryButton } from 'kagr-ui';
import { useSegment } from '../../../hooks/useSegment';
import ViewSegmentAttributes from '../view-segment/view-segment-attributes';

const useStyles = makeStyles((theme) => ({
  selectInputs: {
    margin: '10px 12px',
    '& .MuiInputLabel-root': {
      backgroundColor: '#f8f8f8'
    },
    '& .MuiInputLabel-asterisk': {
      color: colors.rustRed
    }
  },
  headerMargin: {
    margin: 20,
    marginTop: 5
  },
  inputGrouping: {
    flexDirection: 'column'
  },
  segmentCompositionSelector: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    '& .MuiInputBase-root': {
      width: '40%'
    }
  },
  previewSegment: {
    fontSize: 12,
    marginLeft: 10,
    cursor: 'pointer',
    color: blue[600],
    border: 'none',
    padding: 0,
    background: 'none'
  },
  segmentOperatorSelector: {
    width: '15%'
  },
  rowInner: {
    backgroundColor: '#f8f8f8'
  },
  dialog: {
    [theme.breakpoints.up('sm')]: {
      '& .MuiDialog-paperWidthSm': {
        width: '90%',
        maxWidth: '90%',
        height: '90%',
        maxHeight: '90%'
      }
    }
  }
}));

const DependencyAlert = withStyles({
  root: {
    width: '55%',
    marginBottom: 0,
    marginLeft: 12
  }
})(SecondaryAlert);

interface Props {
  exclude: boolean;
  isDependency: boolean;
}

const defaultComposition = {
  segmentId: null,
  segmentOperator: SegmentAttributeOperator.And,
  customers: null,
  segmentName: ''
};

const SegmentCompositionSelector = ({ exclude, isDependency }: Props) => {
  const dependencyMessage = 'You cannot select a segment because this segment is already used by other segments';
  const { segmentId } = useParams<{ segmentId: string }>();
  const { selectedComoposition, changeSelectedComposition } = useBuildSegmentComposition(exclude);

  const classes = useStyles();

  const selectableSegments = useAvailableCompositions(segmentId);

  const [selectionCollapsed, setSelectionCollapsed] = useState<boolean>(false);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string>(selectedComoposition.segmentId?.toString() ?? '');
  const [selectedSegmentOperator, setSelectedSegmentOperator] = useState<string>(
    selectedComoposition.segmentOperator?.toString() ?? ''
  );
  const [segmentPreviewOpen, setSegmentPreviewOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (selectableSegments.data) {
      const selectedSegment = selectableSegments.data.find((data) => data.segmentId.toString() === selectedSegmentId);
      changeSelectedComposition(
        selectedSegment?.segmentId
          ? {
              ...selectedSegment,
              segmentOperator: Number(selectedSegmentOperator)
            }
          : defaultComposition
      );
    }
  }, [selectedSegmentId, selectedSegmentOperator]);

  const removeCompositionSelection = () => {
    setSelectedSegmentId('');
    setSelectedSegmentOperator('');
  };

  const renderFilterValues = (value: string) => {
    const segmentInfo = selectableSegments?.data.find((data) => data.segmentId.toString() === value);
    if (!value || !segmentInfo) {
      return '';
    }

    return (
      <div className={buildStyles.filterChips}>
        <MultiselectChip
          label={segmentInfo.segmentName}
          size="small"
          deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="remove segmentation" />}
          onDelete={removeCompositionSelection}
        />
      </div>
    );
  };

  return (
    <CollapsibleSection
      title="Optional: Segment Selection"
      collapsed={selectionCollapsed}
      onCollapse={setSelectionCollapsed}
      className={classes.headerMargin}
    >
      <div className={styles.row}>
        <div className={classes.rowInner}>
          <div className={clsx(styles.inputGroup, classes.inputGrouping)}>
            {isDependency && <DependencyAlert type="info" message={dependencyMessage} />}
            <FormControl
              size="small"
              variant="outlined"
              className={clsx(classes.selectInputs, classes.segmentCompositionSelector)}
            >
              <InputLabel id="segment-composition-selector-label">Select a Segment</InputLabel>
              <Select
                labelId="segment-composition-selector-label"
                label="Select a Segment"
                id="segment-composition-selector"
                renderValue={renderFilterValues}
                value={
                  selectableSegments?.data && selectableSegments.data.length > 0 && selectedSegmentId
                    ? selectedSegmentId
                    : ''
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSelectedSegmentId(e.target.value.toString());
                  if (!selectedSegmentOperator) {
                    setSelectedSegmentOperator(SegmentAttributeOperator.And.toString());
                  }
                }}
                disabled={isDependency}
              >
                ``
                {selectableSegments?.data &&
                  selectableSegments.data.length > 0 &&
                  selectableSegments.data.map((segmentInfo) => (
                    <MenuItem key={segmentInfo.segmentId} value={segmentInfo.segmentId}>
                      {segmentInfo.segmentName} - {segmentInfo.totalCustomers.toLocaleString()} customers
                    </MenuItem>
                  ))}
              </Select>
              {!!selectedSegmentId && (
                <button className={classes.previewSegment} onClick={() => setSegmentPreviewOpen(true)}>
                  PREVIEW SEGMENT
                </button>
              )}
              <SegmentPreview
                openDialog={segmentPreviewOpen}
                segmentId={selectedSegmentId}
                closePreview={() => setSegmentPreviewOpen(false)}
              />
            </FormControl>
            <FormControl
              size="small"
              variant="outlined"
              className={clsx(classes.selectInputs, classes.segmentOperatorSelector)}
            >
              <InputLabel id="segment-operator-selector-label">Interaction</InputLabel>
              <Select
                labelId="segment-operator-selector-label"
                label="Select a Segment"
                id="segment-operator-selector"
                value={selectedSegmentOperator}
                disabled={!selectedSegmentId || isDependency}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSelectedSegmentOperator(e.target.value.toString())
                }
              >
                <MenuItem value={SegmentAttributeOperator.And}>And</MenuItem>
                <MenuItem value={SegmentAttributeOperator.Or}>Or</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};

export default SegmentCompositionSelector;

type SegmentPreviewProps = {
  openDialog: boolean;
  segmentId: string;
  closePreview: () => void;
};

const SegmentPreview = ({ openDialog, segmentId, closePreview }: SegmentPreviewProps) => {
  const { segmentQuery } = useSegment(segmentId);
  const classes = useStyles();

  return (
    <Dialog open={openDialog} className={classes.dialog}>
      <DialogTitle disableTypography>{segmentQuery.data?.name ?? ''}</DialogTitle>
      <DialogContent dividers>
        {segmentQuery.isLoading && !segmentQuery.data && (
          <div className={segmentsStyles.loading}>
            <CircularProgress />
          </div>
        )}
        {segmentQuery.isError && (
          <div className={segmentsStyles.error}>
            <SecondaryAlert type="error" message={`Failed to fetch segment`} />
          </div>
        )}
        {segmentQuery.data && (
          <ViewSegmentAttributes
            excludedAttributes={segmentQuery.data.attributes.exclude}
            includedAttributes={segmentQuery.data.attributes.include}
            includeComposition={segmentQuery.data.composition?.include}
            excludeComposition={segmentQuery.data.composition?.exclude}
          />
        )}
      </DialogContent>
      <DialogActions>
        <TertiaryButton text="Close" title="Close" component="button" handleClick={closePreview} />
      </DialogActions>
    </Dialog>
  );
};
